import { FORMS } from "@/constants/brand";
import { ArrowUpRight, Heart, Compass, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import { useTranslation } from "@/i18n/LanguageContext";

const portraitImg = "https://images.pexels.com/photos/8469999/pexels-photo-8469999.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const interiorImg = "https://images.pexels.com/photos/5806981/pexels-photo-5806981.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const VALUE_ICONS = [Heart, Compass, Sparkles];

export default function AboutPage() {
    const { t } = useTranslation();
    const cards = t("about.values.cards");
    const cardList = Array.isArray(cards) ? cards : [];

    return (
        <div data-testid="about-page">
            <SEO
                title={t("about.seo.title")}
                description={t("about.seo.description")}
                keywords="women-owned businesses, women in trades, female contractors, our mission, home services directory"
                path="/about"
            />

            <section className="bg-cream">
                <div className="container-x pt-20 lg:pt-28 pb-16">
                    <p className="label-eyebrow">{t("about.hero.eyebrow")}</p>
                    <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-heading tracking-tight leading-[1.05] text-ink max-w-5xl">
                        {t("about.hero.title1")}{" "}
                        <span className="italic text-terracotta">{t("about.hero.titleAccent")}</span>{" "}
                        {t("about.hero.title2")}
                    </h1>
                </div>
            </section>

            <section className="bg-cream-deep">
                <div className="container-x section-y grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6">
                        <div className="rounded-3xl overflow-hidden">
                            <img
                                src={portraitImg}
                                alt="A confident woman professional in the construction trades wearing safety gear"
                                className="w-full h-[520px] object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-6 space-y-6 text-lg text-ink-soft leading-relaxed">
                        <p className="label-eyebrow">{t("about.story.eyebrow")}</p>
                        <p className="text-2xl text-ink font-heading leading-snug">
                            {t("about.story.lead")}
                        </p>
                        <p>{t("about.story.p1")}</p>
                        <p>
                            <span className="text-ink font-medium">{t("about.story.emphasis")}</span>
                            {t("about.story.p2")}
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-cream">
                <div className="container-x section-y">
                    <div className="max-w-3xl">
                        <p className="label-eyebrow">{t("about.values.eyebrow")}</p>
                        <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-heading tracking-tight leading-tight text-ink">
                            {t("about.values.title")}
                        </h2>
                    </div>

                    <div className="mt-14 grid md:grid-cols-3 gap-6">
                        {cardList.map((card, i) => {
                            const Icon = VALUE_ICONS[i] || Heart;
                            return (
                                <div key={card.title} className="bg-white border border-border rounded-2xl p-8 hover:shadow-md transition-shadow">
                                    <span className="w-12 h-12 rounded-full bg-terracotta/10 flex items-center justify-center text-terracotta">
                                        <Icon size={22} />
                                    </span>
                                    <h3 className="mt-6 font-heading text-xl text-ink">{card.title}</h3>
                                    <p className="mt-3 text-ink-soft leading-relaxed">{card.body}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="bg-cream-deep">
                <div className="container-x section-y grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6 order-2 lg:order-1 space-y-6 text-lg text-ink-soft leading-relaxed">
                        <p className="label-eyebrow">{t("about.ahead.eyebrow")}</p>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading tracking-tight leading-tight text-ink">
                            {t("about.ahead.title1")}{" "}
                            <span className="italic text-terracotta">{t("about.ahead.titleAccent")}</span>
                        </h2>
                        <p>{t("about.ahead.p1")}</p>
                        <p>{t("about.ahead.p2")}</p>
                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                            <a
                                href={FORMS.homeownerCommunity}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="about-homeowner-cta"
                                className="inline-flex items-center gap-2 rounded-full bg-ink text-cream font-semibold px-6 py-3.5 hover:bg-ink-soft transition-colors"
                            >
                                {t("about.ahead.ctaHomeowner")}
                                <ArrowUpRight size={18} />
                            </a>
                            <a
                                href={FORMS.foundingProvider}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="about-provider-cta"
                                className="inline-flex items-center gap-2 rounded-full border border-ink/30 text-ink font-semibold px-6 py-3.5 hover:bg-ink hover:text-cream transition-colors"
                            >
                                {t("about.ahead.ctaProvider")}
                                <ArrowUpRight size={18} />
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-6 order-1 lg:order-2">
                        <div className="rounded-3xl overflow-hidden">
                            <img
                                src={interiorImg}
                                alt="A cozy, welcoming home interior with thoughtful design and warm tones"
                                className="w-full h-[480px] object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
